import React, { useEffect, useState } from "react";
import { PropTypes } from "prop-types";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import css from "../../common/anomalyMagneticStyle.less";
import CircuitTrendAvailTable from "./CircuitsAvailability/CircuitTrendAvailTable";
import CircuitTrendAvailChart from "./CircuitsAvailability/CircuitTrendAvailChart";
import CircuitsUsage from "./CircuitsUsage/index";
import Applications from "./Applications/index";
import TopClients from "./TopClients/index";
import TabPanel from "./TabPanel";
import useI18n from "../../../../../../i18n/useI18n";

const [HbrTabGroup, HbrTab] = reactWrapper(["hbr-tab-group", "hbr-tab"]);

const UsageADTabs = props => {
  const { tViews } = useI18n('views')
  const [selectedTab, setSelectedTab] = useState(null);
  const isCircuitTab = selectedTab === "circuitAvailTab" || selectedTab === "circuitUsgTab";
  const sharedCircuitTableHeader = selectedTab === "circuitAvailTab"
    ? tViews('usageBasedAD.availTableHeader')
    : tViews('usageBasedAD.usageTableHeader');
  // Helper function to map tab IDs to readable names
  const getTabDisplayName = (tabId) => {
    switch (tabId) {
      case "circuitUsgTab":
        return tViews('circuit.ddcTitle');
      case "circuitAvailTab":
        return tViews('circuitAvailBarChart');
      case "appTab":
        return tViews('reportApp360Application');
      case "TopClientsTab":
        return tViews('userTableHeadingTopClients');
      default:
        return tViews('circuit.ddcTitle'); // Default fallback
    }
  };

  useEffect(() => {
    const storedTab = localStorage.getItem("selectedUsageADTab");
    const defaultTab = storedTab || "circuitUsgTab";
    setSelectedTab(defaultTab);

    // Set global tab state with readable name.
    window._tab_view = getTabDisplayName(defaultTab);
  }, [props.refresh, props.selectedHour, props.cellularInterfaceProps.site]);

  const onTabsChange = tab => {
    setSelectedTab(tab);
    localStorage.setItem("selectedUsageADTab", tab);

    // Map tab ID to readable value.
    window._tab_view = getTabDisplayName(tab);
  };

  const renderTabs = (tab, label) => {
    return (
      <>
        <HbrTab
          slot="nav"
          id={tab}
          role="tab"
          aria-selected={selectedTab === tab}
          aria-controls={tab}
          active={selectedTab === tab}
          key={tab}
          panel={tab}
          onHbr-active={() => onTabsChange(tab)}
        >
          <div>{label}</div>
        </HbrTab>
      </>
    );
  };

  return (
    <div className={css["ad-tabs-usage"]}>
      <HbrTabGroup aria-label={tViews('a11yLabels.tabsGroupLabel')}>
        {renderTabs(
          'circuitUsgTab',
          tViews('usageBasedAD.circuitUsg')
        )}
        {renderTabs(
          'circuitAvailTab',
          tViews('usageBasedAD.circuitAvail')
        )}
        {renderTabs('appTab', tViews('usageBasedAD.app'))}
        {renderTabs(
          'TopClientsTab',
          tViews('usageBasedAD.topClients')
        )}
      </HbrTabGroup>

      {props.children}

      <TabPanel
        id="circuitAvailTab"
        selectedTab={selectedTab}
      >
        {props.selectedtrendCircuits && (
          <CircuitTrendAvailChart
            circuitList={props.selectedtrendCircuits}
            isCircuitListSame={props.isCircuitListSame}
            circuitTrendList={props.circuitTrendList}
            refresh={props.refresh}
            cuiProps={props.cellularInterfaceProps}
          />
        )}
      </TabPanel>

      <TabPanel id="circuitUsgTab" selectedTab={selectedTab}>
        {props.selectedtrendCircuits && (
          <CircuitsUsage
            selectedHour={props.selectedHour}
            cuiProps={props.cellularInterfaceProps}
            refresh={props.refresh}
            saveSelectedTrendCircuits={props.saveSelectedTrendCircuits}
            selectedtrendCircuits={props.selectedtrendCircuits}
            isPrevUsgList={props.isPrevUsgList}
            prevCircuitUsageList={props.prevCircuitUsageList}
          />
        )}
      </TabPanel>

      {selectedTab && (
        <div
          hidden={!isCircuitTab}
          aria-hidden={!isCircuitTab}
          style={{ display: isCircuitTab ? "block" : "none" }}
        >
          <CircuitTrendAvailTable
            selectedHour={props.selectedHour}
            site={props.cellularInterfaceProps.site}
            refresh={props.refresh}
            saveSelectedTrendCircuits={props.saveSelectedTrendCircuits}
            header={sharedCircuitTableHeader}
          />
        </div>
      )}

      <TabPanel id="appTab" selectedTab={selectedTab}>
        <Applications
          cuiProps={props.cellularInterfaceProps}
          setAppUsgList={props.setAppUsgList}
          appUsageList={props.appUsageList}
          selectedHour={props.selectedHour}
          setPrevAppUsgList={props.setPrevAppUsgList}
          prevAppUsageList={props.prevAppUsageList}
        />
      </TabPanel>

      <TabPanel id="TopClientsTab" selectedTab={selectedTab}>
        <TopClients
          cuiProps={props.cellularInterfaceProps}
          site={props.cellularInterfaceProps.site}
          selectedHour={props.selectedHour}
        />
      </TabPanel>
    </div>
  );
};

UsageADTabs.propTypes = {
  selectedHour: PropTypes.string,
  refresh: PropTypes.bool,
  cellularInterfaceProps: PropTypes.object,
  saveSelectedTrendCircuits: PropTypes.func,
  selectedtrendCircuits: PropTypes.array,
  isCircuitListSame: PropTypes.func,
  circuitTrendList: PropTypes.array,
  isPrevUsgList: PropTypes.func,
  setAppUsgList: PropTypes.func,
  prevCircuitUsageList: PropTypes.array,
  appUsageList: PropTypes.array,
  setPrevAppUsgList: PropTypes.func,
  prevAppUsageList: PropTypes.array,
  children: PropTypes.node
};

export default UsageADTabs;
